import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { dynamicImage } from "../../Service";
import SidebarMain, { PropsTypes } from "./SidebarMain";
import { useAppSelector } from "../../ReaduxToolkit/Hooks";
import { AesDecrypt, keyPromise } from "../../Service/crypto";
import webservice from "../../Service/webservice";
import { Image } from "../../AbstractElements";
import LogoutPage from "./../../Pages/Authentication/Logout";
import SessionTimeout from "./SessionTimeout";
import api from '../../api';


// =========================================================================
// NEW ROBUST TAB TRACKER HOOK
// =========================================================================
function useActiveTabTracker() {
  const tabIdKey = 'app_tab_id';
  const tabListKey = 'app_active_tabs';
  const [activeTabs, setActiveTabs] = useState<string[]>([]);

  useEffect(() => {
    // Assign a unique ID to this tab, stored in sessionStorage to persist through reloads
    const currentTabId = sessionStorage.getItem(tabIdKey) || `${Date.now()}-${Math.random()}`;
    sessionStorage.setItem(tabIdKey, currentTabId);

    // Function to safely get and parse the list of tabs from localStorage
    const getTabs = (): string[] => {
      try {
        const tabs = JSON.parse(localStorage.getItem(tabListKey) || '[]');
        return Array.isArray(tabs) ? tabs : [];
      } catch (e) {
        return [];
      }
    };

    // --- Add this tab to the active list on mount ---
    const initialTabs = getTabs();
    if (!initialTabs.includes(currentTabId)) {
      const newTabs = [...initialTabs, currentTabId];
      localStorage.setItem(tabListKey, JSON.stringify(newTabs));
      setActiveTabs(newTabs);
    } else {
      setActiveTabs(initialTabs);
    }


    // --- Remove this tab from the list on unmount/close ---
    // 'pagehide' is more reliable than 'beforeunload' for this kind of cleanup.
    const handleUnload = () => {
      const tabs = getTabs();
      const newTabs = tabs.filter(id => id !== currentTabId);
      localStorage.setItem(tabListKey, JSON.stringify(newTabs));
    };
    window.addEventListener('pagehide', handleUnload);


    // --- Listen for changes from other tabs to keep the count in sync ---
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === tabListKey) {
        setActiveTabs(getTabs());
      }
    };
    window.addEventListener('storage', handleStorageChange);


    // --- Cleanup function ---
    return () => {
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return activeTabs.length;
}


// =========================================================================
// UPDATED SIDEBAR COMPONENT
// =========================================================================
const Sidebar = ({ setOpen, open, onCloseSidebar }: PropsTypes) => {
    const { toggleSidebar } = useAppSelector((state) => state.layout);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchImage = async () => {
            const Image = localStorage.id;
            const key = await keyPromise;

            if (Image) {
                try {
                    const result = await api.fetch("api/profileUpload/image", {
                        method: "POST",
                        body: JSON.stringify({ Image })
                    });
                    const res = await result.json();
                    const resp = await AesDecrypt(key, res);
                    if (resp && resp.Avatar) {
                        try {
                            const responseImg = await fetch(resp.Avatar);
                            if (responseImg.ok) {
                                const blob = await responseImg.blob();
                                const readerImg = new FileReader();
                                readerImg.onloadend = () => {
                                    const base64data = readerImg.result as string;
                                    localStorage.setItem("Avatar", base64data);
                                };
                                readerImg.readAsDataURL(blob);
                            } else {
                                localStorage.setItem("Avatar", resp.Avatar);
                            }
                        } catch (fetchErr) {
                            // Fallback to direct image URL if CORS or network request fails
                            localStorage.setItem("Avatar", resp.Avatar);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching profile image:", error);
                }
            }
        };
        fetchImage();
    }, []);

    const handleSessionTimeout = () => {
        localStorage.clear();
        window.dispatchEvent(new CustomEvent('auth_state_changed', { detail: { action: 'logout' } }));
        navigate("/login");
    };

    // Use the new, more reliable tab tracker
    const tabCount = useActiveTabTracker();

    const setCatSession = () => {
        if (sessionStorage.getItem('cachingExecuted')) {
            return;
        }

        const IsFromLogin: any = localStorage.getItem('navigationType');
        let storedVersion: any = localStorage.getItem('version');
        const staticVersion = "1.0.0";
        const performance: any = window.performance;
        const navigationType: any = performance.getEntriesByType('navigation')[0].type;
        const isPageReload = navigationType === 'reload';

        if (!storedVersion) {
            localStorage.setItem('version', staticVersion);
            return;
        }

        // This logic now uses a reliable tab count.
        // It will only evaluate to true if this is TRULY the last tab.
        // Duplicating a tab will correctly increment the count, and closing one will
        // correctly decrement it, preventing accidental logouts.
        if (storedVersion !== staticVersion && !isPageReload && IsFromLogin !== 'FromLogin' && tabCount <= 1) {
            handleSessionTimeout();
        } else if (!isPageReload) {
            window.addEventListener('beforeunload', () => {
                let versionParts: any = storedVersion.split('.');
                versionParts[versionParts.length - 1] = parseInt(versionParts[versionParts.length - 1]) + 1;
                const newVersion = versionParts.join('.');
                localStorage.setItem('version', newVersion);
                localStorage.setItem("navigationType", navigationType);
            });
        }

        sessionStorage.setItem('cachingExecuted', 'true');
    };

    setCatSession();

    return (
        <div className={`sidebar-wrapper ${open ? "sidebar-default" : ""} ${toggleSidebar ? "close_icon" : ""}`}>
            <div>
                <div>
                    <Link to={`/dashboard`}>
                        <Image style={{ width: "100%", marginTop: "30%" }}
                            src={dynamicImage("logo/logo.png")} alt="Logo"
                        />
                    </Link>
                    <div className="back-btn">
                        <i className="fa fa-angle-left"></i>
                    </div>
                </div>
                <SidebarMain setOpen={setOpen} open={open} onCloseSidebar={onCloseSidebar} />
                <SessionTimeout onLogout={handleSessionTimeout} />
            </div>
        </div>
    );
};

export default Sidebar;